import React, { useMemo } from 'react';
import { useSearchQuery, useSearchFilters, useSearchPage, useSearchSort } from '../contexts/SearchContext';
import { useDispatch, useSelector } from 'react-redux';
import { addSavedSearch, removeSavedSearch } from '../redux/features/savedSearchesSlice';
import { RootState } from '../redux/store';
import { FormattedMessage } from 'react-intl';
import { useIntl } from 'react-intl';
import { useToast } from '../contexts/ToastContext';
import { useAppStore } from '../redux/hooks';
import { generateUrlFromIndexUiState } from '../utils/algolia/url-handling';
import { getIndexName } from '../constants';
import { usePageContext } from 'vike-react/usePageContext';

export const SaveSearchButton = () => {
  const { query } = useSearchQuery();
  const { filters } = useSearchFilters();
  const { page } = useSearchPage();
  const sort = useSearchSort();
  const pageContext = usePageContext();
  const locale = (pageContext?.locale || 'en') as string;
  const dispatch = useDispatch();
  const store = useAppStore();
  const savedSearches = useSelector((state: RootState) => state.recentSearches?.savedSearches || []);
  const intl = useIntl();
  const { showToast } = useToast();

  // Generate current search URL
  const currentUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const indexName = getIndexName(locale);
    const uiState: any = {
      query: query || '',
      refinementList: typeof filters === 'object' && filters ? filters : {},
      page: Number.isFinite(page) ? page : 0,
      sort,
    };
    const url = generateUrlFromIndexUiState(uiState, indexName, window.location.pathname);
    const fullUrl = window.location.origin + (url && url.length > 0 ? url : window.location.pathname);
    return fullUrl;
  }, [query, filters, page, sort, locale]);

  // Check if the current search is already saved
  const isSaved = Array.isArray(savedSearches) && savedSearches.some((saved) => saved.url === currentUrl);

  const handleSaveSearch = () => {
    const willBeSaved = !isSaved;
    
    if (willBeSaved) {
      dispatch(addSavedSearch(currentUrl));
    } else {
      dispatch(removeSavedSearch(currentUrl));
    }
    
    // Validate state was updated by checking Redux state
    // Use setTimeout to allow Redux state to update
    setTimeout(() => {
      const state = store.getState();
      const updatedSearches = state.recentSearches?.savedSearches || [];
      const isNowSaved = Array.isArray(updatedSearches) && updatedSearches.some((saved) => saved.url === currentUrl);
      
      if (isNowSaved === willBeSaved) {
        if (willBeSaved) {
          showToast(
            intl.formatMessage({ id: 'search.toast.saved', defaultMessage: 'Search saved', description: 'Success toast notification when a search query is saved' }),
            'success'
          );
        } else {
          showToast(
            intl.formatMessage({ id: 'search.toast.unsaved', defaultMessage: 'Search removed', description: 'Info toast notification when a saved search is removed' }),
            'info'
          );
        }
      }
    }, 0);
  };

  return (
    <button
      onClick={handleSaveSearch}
      className={`backdrop-blur bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-full w-10 h-10 flex items-center justify-center shadow hover:bg-gray-100 dark:hover:bg-gray-700 transition @sm:w-auto @sm:h-auto @sm:rounded-lg @sm:px-3 @sm:py-1.5 @sm:gap-2 @sm:text-sm cursor-pointer ${isSaved ? 'ring-2 ring-green-400' : ''}`}
      aria-label={isSaved
        ? intl.formatMessage({ id: 'search.unsave', defaultMessage: 'Remove Saved Search', description: 'Button text to remove a saved search' })
        : intl.formatMessage({ id: 'search.save', defaultMessage: 'Save Search', description: 'Button text to save current search' })
      }
      title={isSaved
        ? intl.formatMessage({ id: 'search.unsave', defaultMessage: 'Remove Saved Search', description: 'Button text to remove a saved search' })
        : intl.formatMessage({ id: 'search.save', defaultMessage: 'Save Search', description: 'Button text to save current search' })
      }
      type="button"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className={`h-5 w-5 ${isSaved ? 'text-green-500' : 'text-gray-400 dark:text-gray-500'}`}
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z"
          clipRule="evenodd"
        />
      </svg>
      <span className="hidden @sm:inline text-black dark:text-white font-medium">
        {isSaved ? (
          <FormattedMessage id="search.unsave" defaultMessage="Remove Saved Search" description="Button text to remove a saved search" />
        ) : (
          <FormattedMessage id="search.save" defaultMessage="Save Search" description="Button text to save current search" />
        )}
      </span>
    </button>
  );
};
